/**
 * [T149-PR3] Shared helpers for skin adapters: testid generation,
 * loading/empty/error visual building blocks, and chart-ready sentinel.
 *
 * Adapters compose these so behaviour stays uniform across the 11 skins.
 *
 * @module dashboard/widgets/skins/_shared
 */
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IconButton } from '@/components/ui/icon-button';
import { AlertTriangle, Clock, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

import type { WidgetKind } from '@/lib/resolverContracts';
import type { SkinStateProps } from './types';
import type { IWidgetDisplay } from '@/lib/types';

/**
 * shouldUseGlass(display): whether the widget should render with the glass-panel CSS class.
 * - `true` → glass ON (uses .glass-panel which adds backdrop-blur + 30% border)
 * - `false` / `undefined` → glass OFF (no .glass-panel; opt-in semantics)
 */
export function shouldUseGlass(display?: IWidgetDisplay): boolean {
  return display?.useGlassStyle === true;
}

/**
 * Extracts the props that `WidgetFrame` accepts for lifecycle / overlay state
 * from the full `SkinStateProps`. Every skin SHOULD call this helper and spread
 * the result into every `<WidgetFrame>` call so that compatibility error overlays,
 * timeout states, no-data overlays, view-mode recovery buttons, refresh
 * indicators, and last-updated timestamps all work correctly.
 *
 * Also extracts `noGlass` from `widget.display.useGlassStyle` so the glass-panel
 * toggle (AC9/AC10) works correctly on the skin path — callers pass the full
 * `SkinProps` object which includes `widget`.
 *
 * [T149-AUDIT Phase 3]
 * @see frontend/src/components/dashboard/widgets/WidgetFrame.tsx
 */
export function useSkinFrameProps(props: SkinStateProps) {
  // Runtime access — every caller passes SkinProps which carries widget.display
  const extended = props as SkinStateProps & {
    widget?: { display?: IWidgetDisplay };
  };
  const noGlass = !shouldUseGlass(extended.widget?.display);
  return {
    compatError: props.compatError,
    isNoData: props.isNoData,
    noDataMessage: props.noDataMessage,
    isTimeout: props.isTimeout,
    isViewMode: props.isViewMode,
    onRequestViewModeFix: props.onRequestViewModeFix,
    onRequestRemove: props.onRequestRemove,
    isRefreshing: props.isRefreshing,
    lastUpdatedAt: props.lastUpdatedAt,
    onRetry: props.onRetry,
    onRequestFix: props.onRequestFix,
    ariaProps: props.ariaProps,
    compatibilityWarnings: props.compatibilityWarnings,
    noGlass,
  };
}

/**
 * Canonical `data-testid` builder for a skin instance.
 *
 * Multi-pin support: each rendered skin instance gets a unique testid
 * suffixed by `widget.id`.
 */
export function skinTestId(kind: WidgetKind, widgetId: string, slot?: 'loading' | 'empty' | 'error'): string {
  return `skin-${kind}-${widgetId}${slot ? `-${slot}` : ''}`;
}

/**
 * Default loading skeleton — three stacked rows. Adapters MAY render
 * a richer skeleton inline if the visual is materially different.
 */
export function SkinLoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-4 w-full" />
      ))}
    </div>
  );
}

/** Shape-matched loading skeleton for KPI skins — tall value + short subtitle. */
export function SkinLoadingKpi() {
  return (
    <div className="flex flex-col gap-2 py-1">
      <Skeleton className="h-8 w-3/5 rounded" />
      <Skeleton className="h-3 w-2/5 rounded" />
    </div>
  );
}

/** Shape-matched loading skeleton for bar / histogram / column chart skins. */
export function SkinLoadingBars({ bars = 6 }: { bars?: number }) {
  const heights = [65, 45, 80, 55, 70, 35, 60, 50].slice(0, bars);
  return (
    <div className="flex flex-col h-full min-h-0 gap-1 pb-1 p-2">
      <div className="flex items-end gap-2 flex-1 min-h-0 px-1">
        {heights.map((h, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-t-sm"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="h-px w-full bg-border/60 shrink-0" />
      <div className="flex gap-2 px-1 mt-1">
        {heights.map((_, i) => (
          <Skeleton key={i} className="flex-1 h-3 rounded" />
        ))}
      </div>
    </div>
  );
}

/** Shape-matched loading skeleton for line / area / trend chart skins. */
export function SkinLoadingLine() {
  return (
    <div className="flex flex-col h-full min-h-0 gap-1 pb-1 p-2">
      <div className="flex-1 relative min-h-0">
        <div className="absolute inset-0 flex items-end gap-px">
          <div className="relative flex-[2] h-full flex items-end">
            <Skeleton className="w-full rounded-t-lg" style={{ height: '40%' }} />
          </div>
          <div className="relative flex-[3] h-full flex items-end">
            <Skeleton className="w-full rounded-t-xl" style={{ height: '75%' }} />
          </div>
          <div className="relative flex-[2] h-full flex items-end">
            <Skeleton className="w-full rounded-t-lg" style={{ height: '55%' }} />
          </div>
          <div className="relative flex-[2] h-full flex items-end">
            <Skeleton className="w-full rounded-t-md" style={{ height: '30%' }} />
          </div>
        </div>
      </div>
      <div className="h-px w-full bg-border/60 shrink-0" />
      <div className="flex gap-2 px-1 mt-1">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="flex-1 h-3 rounded" />
        ))}
      </div>
    </div>
  );
}

/** Shape-matched loading skeleton for donut / pie chart skins. */
export function SkinLoadingDonut() {
  return (
    <div className="flex flex-col items-center gap-3 py-2 h-full justify-center">
      <div className="relative w-24 h-24 shrink-0">
        <Skeleton className="w-24 h-24 rounded-full" />
        <div className="absolute inset-[25%] rounded-full bg-card" />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 w-full px-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Skeleton className="w-3 h-3 rounded-sm shrink-0" />
            <Skeleton className="h-3 flex-1 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Shape-matched loading skeleton for radar / spider chart skins. */
export function SkinLoadingRadar() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 h-full py-2">
      <div className="relative w-32 h-32 shrink-0 flex items-center justify-center">
        <Skeleton className="w-28 h-28 rounded-full opacity-30" />
        <Skeleton className="absolute w-20 h-20 rounded-full opacity-50" />
        <Skeleton className="absolute w-12 h-12 rounded-full opacity-70" />
      </div>
      <div className="grid grid-cols-3 gap-1 w-full max-w-[200px]">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-3 rounded" style={{ width: `${50 + (i % 3) * 20}%` }} />
        ))}
      </div>
    </div>
  );
}

/** Shape-matched loading skeleton for calendar / heatmap skins. */
export function SkinLoadingHeatmap() {
  return (
    <div className="flex flex-col gap-1 p-2 h-full">
      <div className="flex gap-1 mb-1">
        {[...Array(7)].map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1 rounded" />
        ))}
      </div>
      {[...Array(5)].map((_, week) => (
        <div key={week} className="flex gap-1 flex-1">
          {[...Array(7)].map((_, day) => (
            <Skeleton key={day} className="flex-1 min-h-[12px] rounded-[3px]" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Shape-matched loading skeleton for progress / budget bar skins. */
export function SkinLoadingProgress({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3 py-1 px-2">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <div className="flex justify-between items-center">
            <Skeleton className="h-3.5 rounded" style={{ width: `${35 + i * 15}%` }} />
            <Skeleton className="h-3.5 w-10 rounded" />
          </div>
          <Skeleton className="h-2.5 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Shape-matched loading skeleton for list / table skins. */
export function SkinLoadingList({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-0 px-2 py-1">
      <div className="flex gap-3 pb-2 mb-1 border-b border-border/50">
        {[60, 40, 30].map((w, i) => (
          <Skeleton key={i} className="h-3.5 rounded" style={{ flex: `${w} 0 0%` }} />
        ))}
      </div>
      {[...Array(rows)].map((_, row) => (
        <div key={row} className="flex gap-3 py-2 border-b border-border/30">
          {[55, 45, 25].map((w, col) => (
            <Skeleton key={col} className="h-3 rounded" style={{ flex: `${w} 0 0%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Skin-owned "coming soon" state. Used for stub widgets pending resolver/skin implementation.
 * [T149-AUDIT-P0] Replaces perpetual SkinLoadingRows in AmountHistogramBarSkin + RecurringVsDiscretionarySkin.
 * Auto-density: ≥120px = icon+title+desc+badge, 70–119px = title+badge only, <70px = Clock icon only.
 */
export function SkinComingSoonState({
  title = 'Coming soon',
  description = 'This widget is coming in a future update.',
}: {
  title?: string;
  description?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [measuredDensity, setMeasuredDensity] = useState<'standard' | 'compact' | 'icon-only'>('standard');

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height ?? 0;
      setMeasuredDensity(h < 70 ? 'icon-only' : h < 120 ? 'compact' : 'standard');
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="flex flex-col items-center justify-center text-center h-full gap-2 py-4">
      {measuredDensity !== 'compact' && (
        <Clock className="h-6 w-6 text-muted-foreground/40" aria-hidden="true" />
      )}
      {measuredDensity !== 'icon-only' && (
        <>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {measuredDensity === 'standard' && (
            <p className="text-xs text-muted-foreground/60 max-w-[160px]">{description}</p>
          )}
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-muted-foreground/60">
            Coming soon
          </Badge>
        </>
      )}
      {measuredDensity === 'icon-only' && (
        <span className="sr-only">{title} — coming soon</span>
      )}
    </div>
  );
}

interface SkinEmptyStateProps {
  /** Short headline shown to the user. */
  title: string;
  /** Body copy explaining why the skin is empty + how to populate it. */
  description: string;
  /** Optional manual-override CTA (per FinTrack user-flexibility mandate). */
  cta?: { label: string; to: string };
  /** Optional icon node rendered above the title. */
  icon?: ReactNode;
  /**
   * Initial-paint density fallback before the {@link ResizeObserver}
   * measures the host. Auto-density takes over after the first
   * measurement.
   *
   * @deprecated Density now derives from the rendered container height
   *   via {@link https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver ResizeObserver}.
   *   Continue passing `density="compact"` from KPI skins as the SSR
   *   fallback to avoid first-paint overflow.
   */
  density?: 'standard' | 'compact';
}

type EmptyDensity = 'standard' | 'compact' | 'icon-only';

/**
 * Skin-owned empty state. Replaces the legacy `<FallbackWidget>` apology card.
 *
 * Per [user-flexibility mandate](.github/copilot-instructions.md), every
 * empty state offers a clearly visible "do it manually" CTA into the
 * underlying feature page.
 *
 * [T149-PR8-CONT-6] Auto-density: a `ResizeObserver` measures the rendered
 * container and switches between `standard` (≥140px), `compact` (80–139px),
 * and `icon-only` (<80px) tiers. The optional `density` prop only seeds the
 * initial paint before the observer reports.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 */
export function SkinEmptyState({
  title,
  description,
  cta,
  icon,
  density,
}: SkinEmptyStateProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialDensity: EmptyDensity = density ?? 'standard';
  const [measuredDensity, setMeasuredDensity] = useState<EmptyDensity | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const compute = (h: number): EmptyDensity => {
      if (h < 80) return 'icon-only';
      if (h < 140) return 'compact';
      return 'standard';
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const next = compute(entry.contentRect.height);
      setMeasuredDensity((prev) => (prev === next ? prev : next));
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const effectiveDensity: EmptyDensity = measuredDensity ?? initialDensity;
  const isCompact = effectiveDensity === 'compact';
  const isIconOnly = effectiveDensity === 'icon-only';

  // Memoize fallback CTA icon node to avoid re-renders churning IconButton refs.
  const ctaIcon = useMemo(() => <Plus className="h-4 w-4" aria-hidden="true" />, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'h-full min-h-0 overflow-hidden',
        isIconOnly
          ? 'px-1 py-1 gap-0'
          : isCompact
          ? 'px-1 py-1.5 gap-1'
          : 'py-6 gap-2',
      )}
    >
      {isIconOnly ? (
        <>
          <span className="sr-only">{title}</span>
          <span className="sr-only">{description}</span>
          {cta ? (
            <IconButton
              asChild
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0"
              tooltip={title}
              aria-label={cta.label}
              data-testid="skin-empty-cta-icon"
            >
              <Link to={cta.to}>{ctaIcon}</Link>
            </IconButton>
          ) : (
            <span aria-hidden="true" className="text-muted-foreground">
              {ctaIcon}
            </span>
          )}
        </>
      ) : (
        <>
          {icon && <div className={cn(isCompact && 'shrink-0')}>{icon}</div>}
          <p
            className={cn(
              'font-medium break-words',
              isCompact ? 'max-w-full text-xs leading-tight line-clamp-1' : 'text-sm',
            )}
          >
            {title}
          </p>
          <p
            className={cn(
              'text-muted-foreground break-words',
              isCompact
                ? 'max-w-full text-[11px] leading-snug line-clamp-1 text-ellipsis'
                : 'max-w-sm text-xs',
            )}
          >
            {description}
          </p>
          {cta && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className={cn(
                isCompact
                  ? 'mt-1 h-11 min-h-[44px] min-w-[44px] max-w-full shrink-0 px-2 text-[11px] leading-none'
                  : 'mt-2 min-h-[44px]',
              )}
            >
              <Link
                to={cta.to}
                className={cn(isCompact && 'max-w-full overflow-hidden text-ellipsis whitespace-nowrap')}
              >
                {cta.label}
              </Link>
            </Button>
          )}
        </>
      )}
    </div>
  );
}

interface SkinErrorStateProps {
  message: string;
  onRetry?: () => void;
}

/**
 * Skin-owned error state. Surfaces the PR2 800ms-timeout structured error.
 * [T149-AUDIT-UX] Added AlertTriangle icon for visual hierarchy.
 */
export function SkinErrorState({ message, onRetry }: SkinErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-6 gap-2">
      <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
      <p className="text-sm font-medium text-destructive">Couldn’t load this widget</p>
      <p className="text-xs text-muted-foreground max-w-sm">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} size="sm" variant="outline" className="mt-2 min-h-[44px]">
          Retry
        </Button>
      )}
    </div>
  );
}
